import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import BrandLogo from './BrandLogo';

/**
 * A group's children all drive one query param on one screen — `status` on
 * /library and `tab` on /payments. That is what makes the
 * submenu real rather than decorative: each page reads its param on mount and
 * when it changes, so the child both navigates and selects.
 */
const Sidebar = ({ user, userRole, onLogout, open = false, onClose }) => {
    const isSuperAdmin = userRole === 'super_admin';
    const location = useLocation();
    const params = new URLSearchParams(location.search);

    // Only groups the user has opened or closed by hand. Everything else
    // follows the current route.
    const [toggled, setToggled] = useState({});

    // Landing on a different screen drops those manual choices, so the group
    // that owns the new route is open when you get there — including when the
    // header's search box does the navigating.
    useEffect(() => setToggled({}), [location.pathname]);

    const icons = {
        dashboard: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
        ),
        users: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
        ),
        reports: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
        ),
        payments: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
        ),
        receipts: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
        ),
        team: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
        ),
        activity: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
        ),
        deleteRequests: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6" />
            </svg>
        ),
        settings: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
        )
    };

    const navItems = [
        { path: '/', icon: icons.dashboard, label: 'Dashboard' },
        {
            key: 'libraryUsers',
            icon: icons.users,
            label: 'Library Users',
            basePath: '/library',
            param: 'status',
            children: [
                { label: 'All Libraries', value: null },
                { label: 'Active Libraries', value: 'active' },
                { label: 'Inactive Libraries', value: 'inactive' }
            ]
        },
        { path: '/reports', icon: icons.reports, label: 'Reports' },
        {
            key: 'plansAndCoupon',
            icon: icons.payments,
            label: 'Plans & Coupon',
            basePath: '/payments',
            param: 'tab',
            superAdminOnly: true,
            children: [
                { label: 'Subscription Plans', value: 'plans' },
                { label: 'Coupons', value: 'coupons' },
                { label: 'Plan Analytics', value: 'analytics' },
                { label: 'Platform Pricing', value: 'pricing' }
            ]
        },
        { path: '/payment-receipts', icon: icons.receipts, label: 'Payment Receipts', superAdminOnly: true },
        { path: '/team', icon: icons.team, label: 'Team Management', superAdminOnly: true },
        { path: '/activity', icon: icons.activity, label: 'Activity Logs' },
        { path: '/delete-requests', icon: icons.deleteRequests, label: 'Delete Requests', superAdminOnly: true },
        { path: '/settings', icon: icons.settings, label: 'Settings' }
    ];

    const childPath = (group, child) => child.path || group.basePath;

    const childTo = (group, child) => {
        const path = childPath(group, child);
        return child.value ? `${path}?${group.param}=${child.value}` : path;
    };

    const isChildActive = (group, child) => {
        if (location.pathname !== childPath(group, child)) return false;
        return (params.get(group.param) || null) === (child.value || null);
    };

    const isGroupActive = (group) => group.children.some((child) => isChildActive(group, child))
        || location.pathname === group.basePath;

    const isGroupOpen = (group) => (toggled[group.key] !== undefined ? toggled[group.key] : isGroupActive(group));

    const toggleGroup = (group) => setToggled((prev) => ({ ...prev, [group.key]: !isGroupOpen(group) }));

    const linkClass = (active) =>
        `flex min-h-[46px] items-center space-x-3 px-3.5 py-2.5 rounded-xl transition-all ${
            active ? 'bg-blue-600 text-white shadow-lg shadow-black/10' : 'text-indigo-200 hover:bg-white/5 hover:text-white'
        }`;

    return (
      <>
        {open && <button type="button" aria-label="Close navigation" onClick={onClose} className="fixed inset-0 z-30 h-auto bg-indigo-950/55 backdrop-blur-sm lg:hidden" />}
        <aside className={`fixed inset-y-0 left-0 z-40 flex h-screen w-[286px] flex-col bg-indigo-950 text-white shadow-2xl transition-transform duration-200 lg:relative lg:z-0 lg:translate-x-0 lg:shadow-none ${open ? 'translate-x-0' : '-translate-x-full'}`}>
            <div className="flex min-h-[84px] items-center justify-between border-b border-white/10 px-5">
                <div><BrandLogo width={190} height={58} lightBackground /><p className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-indigo-300">Control centre</p></div>
                <button type="button" aria-label="Close menu" onClick={onClose} className="flex h-11 w-11 items-center justify-center rounded-xl text-indigo-300 hover:bg-white/10 hover:text-white lg:hidden">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
            </div>

            <nav aria-label="Primary navigation" className="flex-1 px-3 py-5 space-y-1.5 overflow-y-auto">
                <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-indigo-400">Platform</p>
                {navItems.map((item) => {
                    if (item.superAdminOnly && !isSuperAdmin) return null;

                    if (!item.children) {
                        return (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                end={item.path === '/'}
                                className={({ isActive }) => linkClass(isActive)}
                                onClick={onClose}
                            >
                                {item.icon}
                                <span className="font-medium">{item.label}</span>
                            </NavLink>
                        );
                    }

                    const open = isGroupOpen(item);
                    const groupActive = isGroupActive(item);

                    return (
                        <div key={item.key}>
                            <button
                                type="button"
                                onClick={() => toggleGroup(item)}
                                aria-expanded={open}
                                className={`w-full ${linkClass(groupActive && !open)} justify-between`}
                            >
                                <span className="flex items-center space-x-3">
                                    {item.icon}
                                    <span className="font-medium">{item.label}</span>
                                </span>
                                <svg
                                    className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {open && (
                                <div className="mt-1 ml-5 pl-4 border-l border-white/10 space-y-1">
                                    {item.children.map((child) => {
                                        const active = isChildActive(item, child);
                                        return (
                                            <NavLink
                                                key={child.label}
                                                to={childTo(item, child)}
                                                className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                                                    active
                                                        ? 'bg-blue-600 text-white font-medium'
                                                        : 'text-indigo-300 hover:bg-white/5 hover:text-white'
                                                }`}
                                                onClick={onClose}
                                            >
                                                {child.label}
                                            </NavLink>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </nav>

            <div className="border-t border-white/10 p-4">
                <div className="mb-3 flex items-center space-x-3 rounded-2xl bg-white/5 p-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600">
                        <span className="text-sm font-semibold">
                            {user?.displayName?.charAt(0)?.toUpperCase()}
                        </span>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{user?.displayName}</div>
                        <div className="text-xs text-indigo-300 truncate">{user?.email}</div>
                    </div>
                </div>
                <button
                    onClick={onLogout}
                    className="w-full flex min-h-[44px] items-center justify-center space-x-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-indigo-100 transition hover:bg-white/10 hover:text-white"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span className="text-sm font-medium">Logout</span>
                </button>
            </div>
        </aside>
      </>
    );
};

export default Sidebar;
